import { Download } from 'lucide-react';
import { useReports, buildReportDownloadUrl } from '@/lib/clientApi';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function monthLabel(iso: string): string {
  const d = new Date(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function generatedLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function triggerDownload(id: string, format: 'pdf' | 'csv'): void {
  window.location.href = buildReportDownloadUrl(id, format);
}

export default function ReportsPage() {
  const { data, isLoading, isError } = useReports();

  const content = (() => {
    if (isLoading) {
      return (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead>Generated</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[0, 1, 2].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-28" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-8 w-32" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    if (isError || !data) {
      return (
        <Alert variant="destructive">
          <AlertDescription>Couldn&apos;t load reports — try again.</AlertDescription>
        </Alert>
      );
    }

    if (data.reports.length === 0) {
      return (
        <div className="py-12 text-center">
          <h2 className="text-lg font-semibold">No reports yet.</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Monthly reports are generated on the 1st of each month. Your first report will appear
            here next month.
          </p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            <TableHead>Generated</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.reports.map((r) => {
            const label = monthLabel(r.periodStart);
            return (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{label}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {generatedLabel(r.createdAt)}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => triggerDownload(r.id, 'pdf')}
                      aria-label={`Download ${label} report as PDF`}
                    >
                      <Download className="mr-1 h-4 w-4" />
                      PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => triggerDownload(r.id, 'csv')}
                      aria-label={`Download ${label} report as CSV`}
                    >
                      <Download className="mr-1 h-4 w-4" />
                      CSV
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  })();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Monthly Reports</h1>
        <p className="text-sm text-muted-foreground">Download your completed items reports.</p>
      </div>
      <div className="overflow-x-auto rounded-md border bg-background">{content}</div>
    </div>
  );
}
